import { useEffect, useRef, useState } from "react";
import { coordsMovedMeters } from "../../../shared/geo";
import { SOCIAL_OPTIONS } from "../../../shared/moods";
import { computeRadioMaximoKm, engineRadii, rankedItemsToPlanCards, searchPlansEngine } from "../../../shared/planEngine";
import { recommendPlansSmartDetailed } from "../../../shared/plansAi";
import { fitsBudget, fitsTime, formatBudget, formatMinutes, parseBudget, parseFreeTime, planFiltersKey } from "../../../shared/planFilters";
import { PlanLimitsInputs, type PlanLimitsValue } from "../components/PlanLimitsInputs";
import {
  placesForPlan,
  PLACE_KIND_LABELS,
  type SettingPref,
} from "../../../shared/plans";
import { fetchNearbyPlacesCascade, leafletMapHtml, planMapsUrl, type TravelMode } from "../../../shared/places";
import type { NearbyPlace, SocialContext } from "../../../shared/types";
import { useLiveLocation } from "../lib/useLiveLocation";
import { useCategories } from "../lib/useCategories";
import { CategoryFilter } from "../components/CategoryFilter";
import { describeCategories, filterPlacesByCategories, pickAcrossCategories } from "../../../shared/categories";
import { readKit } from "../lib/kv";

type Plan = ReturnType<typeof rankedItemsToPlanCards>[number];

const SURPRISE_TIME_SHORTCUTS = [60, 120, 180, 240, 300];
const MODES: { key: TravelMode; label: string }[] = [
  { key: "walk", label: "A pie" },
  { key: "run", label: "Corriendo" },
  { key: "bike", label: "En bici" },
  { key: "car", label: "En coche" },
];

export default function SurprisePlan() {
  const { coords, error: gpsError, ready: gpsReady } = useLiveLocation();
  const [limits, setLimits] = useState<PlanLimitsValue>({ hours: "3", minutes: "", budget: "" });
  const [mode, setMode] = useState<TravelMode>("walk");
  const [social, setSocial] = useState<SocialContext>("amigos");
  const [setting, setSetting] = useState<SettingPref>("any");
  const [categories, setCategories] = useCategories();
  const [busy, setBusy] = useState(false);
  const [stops, setStops] = useState<Plan[]>([]);
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [coordsLabel, setCoordsLabel] = useState("Activando GPS en vivo…");
  const [formError, setFormError] = useState("");
  const [lastSearchKey, setLastSearchKey] = useState<string | null>(null);
  const [stopMinutes, setStopMinutes] = useState(0);
  const lastFetch = useRef<{ latitude: number; longitude: number; radius: number; places: NearbyPlace[] } | null>(null);
  const rollGen = useRef(0);
  const time = parseFreeTime(limits.hours, limits.minutes);
  const budget = parseBudget(limits.budget);
  const currentKey = planFiltersKey({
    minutes: time.ok ? time.minutes : null,
    budget: budget.ok ? budget.budget : "invalid",
    mode,
    setting,
    social,
    categories,
  });
  const filtersChanged = lastSearchKey != null && lastSearchKey !== currentKey;

  // Solo mostramos el estado de la ubicación: no se busca nada hasta pulsar «Tira del hilo».
  useEffect(() => {
    if (lastSearchKey != null) return; // no pisar el resumen de la última sorpresa
    if (!coords) {
      if (gpsReady) setCoordsLabel(gpsError || "Permite la ubicación en el candado del navegador.");
      return;
    }
    const how =
      coords.source === "ip" ? "ubicación aproximada por red" : `GPS en vivo (±${Math.round(coords.accuracyMeters || 0)} m)`;
    setCoordsLabel(`Ubicación lista (${how}).`);
  }, [coords, gpsReady, gpsError, lastSearchKey]);

  const visiblePlaces = places;

  const roll = async () => {
    setFormError("");
    if (!time.ok) {
      setFormError(time.error);
      return;
    }
    if (!budget.ok) {
      setFormError(budget.error);
      return;
    }
    if (!coords) {
      setFormError(gpsError || "No pude leer la ubicación. Permite ubicación y reintenta.");
      return;
    }
    const minutes = time.minutes;
    const maxBudgetEur = budget.budget;
    const searchKey = currentKey;
    const gen = ++rollGen.current;
    setBusy(true);
    try {
      const kit = await readKit();
      const hour = new Date().getHours();
      const mood = hour < 11 ? 0.3 : hour > 20 ? 0.72 : 0.52;
      const budgetStops = Math.max(1, Math.min(5, Math.floor(minutes / 45)));
      const radio = computeRadioMaximoKm(mode, minutes / 60);
      const prev = lastFetch.current;
      let pool: NearbyPlace[];
      if (prev && prev.radius === radio.radioMaximoMeters && coordsMovedMeters(prev, coords) < 120) {
        pool = prev.places;
      } else {
        setCoordsLabel("Buscando sitios en tu radio…");
        const fetched = await fetchNearbyPlacesCascade({ ...coords, radii: engineRadii(radio.radioMaximoMeters) });
        pool = fetched.places;
        lastFetch.current = { latitude: coords.latitude, longitude: coords.longitude, radius: radio.radioMaximoMeters, places: pool };
      }
      if (gen !== rollGen.current) return;
      const fitting = placesForPlan(filterPlacesByCategories(pool, categories), { social, setting, freeOnly: maxBudgetEur === 0 }).filter(
        (p) => fitsBudget(p, maxBudgetEur) && fitsTime(p, minutes, mode),
      );
      setPlaces(fitting);
      const engine = await searchPlansEngine({
        lat: coords.latitude,
        lng: coords.longitude,
        transporte: mode,
        minutes,
        setting,
        categories,
        maxBudgetEur,
        social,
        mood,
        kit,
        places: pool,
      });
      if (gen !== rollGen.current) return;
      let all = rankedItemsToPlanCards(engine.items, minutes);
      let aiNote = "";
      if (all.length < budgetStops) {
        const smart = await recommendPlansSmartDetailed({
          mood,
          social,
          places: fitting,
          kit,
          hour,
          maxBudgetEur,
          setting,
          minutes,
          transporte: mode,
          origin: coords,
        });
        if (gen !== rollGen.current) return;
        aiNote = smart.note ? ` ${smart.note}` : "";
        all = [...all, ...smart.cards.filter((p) => !all.some((n) => n.title === p.title))];
      }
      if (categories.length) all = all.filter((p) => p.map);
      // Al azar, pero repartido entre las categorías marcadas (algo de cada una).
      const picked = pickAcrossCategories(all.filter((p) => p.map), categories, budgetStops);
      const chosen = picked.length ? picked : pickAcrossCategories(all, categories, budgetStops);
      setStops(chosen);
      setStopMinutes(chosen.length ? Math.max(10, Math.floor(minutes / chosen.length)) : 0);
      setLastSearchKey(searchKey);
      setCoordsLabel(
        chosen.length
          ? `Sorpresa para ${formatMinutes(minutes)} · ${categories.length ? `${describeCategories(categories)} · ` : ""}presupuesto ${formatBudget(maxBudgetEur)} · radio ${engine.radioMaximoKm.toFixed(1)} km.${aiNote}`
          : `Nada${categories.length ? ` de «${describeCategories(categories)}»` : ""} encaja con ${formatMinutes(minutes)} y presupuesto ${formatBudget(maxBudgetEur)}. Prueba con más tiempo o presupuesto.${aiNote}`,
      );
    } catch {
      if (gen === rollGen.current) setCoordsLabel("No pude mirar la zona ahora (sin conexión o servicio caído). Reintenta en unos segundos.");
    } finally {
      if (gen === rollGen.current) setBusy(false);
    }
  };

  const mapHtml = coords
    ? leafletMapHtml(
        coords,
        coords,
        stops
          .filter((stop) => stop.map)
          .map((stop) => ({
            latitude: stop.map!.lat,
            longitude: stop.map!.lon,
            name: stop.placeName || stop.title,
          })),
        visiblePlaces.slice(0, 24).map((place) => ({
          latitude: place.latitude,
          longitude: place.longitude,
          name: `${place.name} · ${PLACE_KIND_LABELS[place.kind] || place.kind}`,
        })),
      )
    : "";

  return (
    <div className="page-surprise">
      <section className="surprise-hero">
        <p className="eyebrow">DADO URBANO</p>
        <h1 className="section-title">
          <span className="brand-inline">HERMES</span> tira del hilo
        </h1>
        <p className="lead">Paradas concretas de tu radio, filtradas por compañía, presupuesto y entorno.</p>
      </section>

      {coords ? (
        <div className="map-frame">
          <iframe
            key={`${coords.latitude.toFixed(4)},${visiblePlaces.length},${stops.length}`}
            title="Sitios cerca"
            srcDoc={mapHtml}
            sandbox="allow-scripts allow-same-origin"
            allow="geolocation"
            loading="eager"
          />
        </div>
      ) : null}

      <section className="surprise-controls">
        <PlanLimitsInputs value={limits} onChange={setLimits} timeShortcuts={SURPRISE_TIME_SHORTCUTS} idPrefix="sorpresa" />
        <p className="label">Cómo vas</p>
        <div className="time-row">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`time-chip${mode === m.key ? " on" : ""}`}
              onClick={() => setMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="social-grid">
          {SOCIAL_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`social-chip${social === opt.key ? " on" : ""}`}
              onClick={() => setSocial(opt.key)}
            >
              <span aria-hidden>{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>

        <CategoryFilter selected={categories} onChange={setCategories} />

        <div className="toggle-row">
          <label>
            <input
              type="radio"
              name="sur-setting"
              checked={setting === "any"}
              onChange={() => setSetting("any")}
            />
            Da igual
          </label>
          <label>
            <input
              type="radio"
              name="sur-setting"
              checked={setting === "indoor"}
              onChange={() => setSetting("indoor")}
            />
            Interiores
          </label>
          <label>
            <input
              type="radio"
              name="sur-setting"
              checked={setting === "outdoor"}
              onChange={() => setSetting("outdoor")}
            />
            Exteriores
          </label>
        </div>


        {visiblePlaces.length ? (
          <div className="place-strip">
            {visiblePlaces.slice(0, 10).map((place) => (
              <span key={place.id} className="place-pill">
                <strong>{place.name}</strong>
                <span>{PLACE_KIND_LABELS[place.kind] || place.kind}</span>
              </span>
            ))}
          </div>
        ) : null}

        <p className="coords">{coordsLabel}</p>
        {formError ? (
          <p className="limits-error" role="alert">
            {formError}
          </p>
        ) : null}
        {filtersChanged ? (
          <p className="filters-changed">Has cambiado los filtros. Vuelve a tirar para actualizar la sorpresa.</p>
        ) : null}
        <button type="button" className="btn-ink" onClick={() => void roll()} disabled={busy}>
          {busy ? "Mirando la zona…" : "Tira del hilo"}
        </button>
      </section>

      {stops.length > 0 ? (
        <ol className="surprise-stops">
          {stops.map((stop, index) => {
            const href = planMapsUrl(coords, stop, mode);
            return (
              <li key={`${stop.title}-${index}`}>
                <span className="stop-n">{index + 1}</span>
                <div>
                  {href ? (
                    <a className="stop-link" href={href} target="_blank" rel="noreferrer">
                      <h3>{stop.title}</h3>
                      <p>{stop.detail}</p>
                      <p className="plan-meta">
                        ~{Math.min(stop.minutes, stopMinutes || stop.minutes)} min
                        {stop.price != null ? (stop.price > 0 ? ` · ~${stop.price} €` : " · gratis") : ""} · abrir Google Maps
                      </p>
                    </a>
                  ) : (
                    <>
                      <h3>{stop.title}</h3>
                      <p>{stop.detail}</p>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}
